import { useEffect, useState } from 'react'
import { ApiError, api } from '../api/client'
import type { EventLiveness, ParameterCoverage, TrackedEvent } from '../api/types'
import { Banner } from './Banner'
import { ConfigList, ConfigRow, type RowChip } from './ConfigList'
import { Drawer } from './Drawer'

interface Props {
  propertyId?: string
}

function blankEvent(): TrackedEvent {
  return {
    event_name: '',
    label: '',
    rate_against: null,
    rate_label: '',
    silent_after_hours: null,
    minimum_expected: null,
    parameters: [],
    enabled: true,
  }
}

/** A coverage report as a chip: the row shows trouble, the drawer explains it. */
function coverageChip(report: ParameterCoverage): RowChip {
  const broken = report.state === 'not_set_only' || report.state === 'barely_arriving'
  const figure = report.coverage_percent === null ? '—' : `${report.coverage_percent}%`
  return {
    key: report.parameter,
    text: `${report.parameter} ${figure}`,
    tone: broken ? 'critical' : report.state === 'dropped' ? 'warning' : 'neutral',
  }
}

function summarise(event: TrackedEvent, liveness?: EventLiveness): string {
  const parts: string[] = [
    event.silent_after_hours
      ? `Silent after ${event.silent_after_hours}h`
      : `Silent after ${liveness?.watch_hours ?? 6}h (default)`,
  ]
  if (event.rate_against) parts.push(`rate vs ${event.rate_against}`)
  if (event.parameters.length > 0) {
    parts.push(
      `${event.parameters.length} required parameter${event.parameters.length === 1 ? '' : 's'}`,
    )
  }
  return parts.join(' · ')
}

/**
 * Editor for the events reported in every briefing.
 *
 * Separate from watches on purpose: a watch asks "alert me when this breaks", a
 * tracked event asks "always tell me how this did".
 *
 * One row per event, fields behind a drawer. This list is read far more often than it
 * is edited — usually to check whether a parameter is still arriving — and that reading
 * was previously buried under a full editor card per event.
 */
export function TrackedEvents({ propertyId }: Props) {
  const [events, setEvents] = useState<TrackedEvent[] | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [notice, setNotice] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)
  const [dirty, setDirty] = useState(false)
  const [editing, setEditing] = useState<number | null>(null)
  // The pulse is what knows each event's actual rhythm, so the suggestion has to come
  // from there rather than from the stored configuration. Never fatal: a pulse that
  // cannot be fetched costs the suggestions and nothing else on this screen.
  const [pulse, setPulse] = useState<Record<string, EventLiveness>>({})
  // Measured coverage per event, so a parameter's normal level is visible rather than
  // guessed at. Never fatal: without it the fields still work, unannotated.
  const [coverage, setCoverage] = useState<Record<string, ParameterCoverage[]>>({})

  useEffect(() => {
    const controller = new AbortController()
    api
      .trackedEvents({ propertyId }, controller.signal)
      .then(setEvents)
      .catch((cause: unknown) => {
        if (controller.signal.aborted) return
        setError(cause instanceof Error ? cause.message : 'Could not load tracked events.')
      })
    return () => controller.abort()
  }, [propertyId])

  useEffect(() => {
    const controller = new AbortController()
    api
      .pulse({ propertyId }, controller.signal)
      .then((report) =>
        setPulse(Object.fromEntries(report.events.map((event) => [event.event_name, event]))),
      )
      .catch(() => setPulse({}))
    return () => controller.abort()
  }, [propertyId])

  useEffect(() => {
    const controller = new AbortController()
    api
      .coverage({ propertyId }, controller.signal)
      .then((reports) => {
        const grouped: Record<string, ParameterCoverage[]> = {}
        for (const report of reports) {
          grouped[report.event_name] = [...(grouped[report.event_name] ?? []), report]
        }
        setCoverage(grouped)
      })
      .catch(() => setCoverage({}))
    return () => controller.abort()
  }, [propertyId])

  function update(index: number, patch: Partial<TrackedEvent>) {
    setEvents((current) =>
      current ? current.map((item, i) => (i === index ? { ...item, ...patch } : item)) : current,
    )
    setDirty(true)
    setNotice(null)
  }

  async function save() {
    if (!events) return
    setSaving(true)
    setError(null)
    setNotice(null)

    const prepared = events.map((item) => ({
      ...item,
      event_name: item.event_name.trim(),
      label: item.label.trim() || item.event_name.trim(),
    }))

    try {
      setEvents(await api.saveTrackedEvents(prepared, { propertyId }))
      setDirty(false)
      setEditing(null)
      setNotice('Saved. Cached briefings were dropped, so the next one refetches from GA4.')
    } catch (cause) {
      setError(cause instanceof ApiError ? cause.message : 'Could not save tracked events.')
    } finally {
      setSaving(false)
    }
  }

  if (error && !events) return <Banner tone="critical">{error}</Banner>
  if (!events) return <p className="placeholder">Loading tracked events…</p>

  const open = editing === null ? undefined : events[editing]

  return (
    <>
      <p className="section__note" style={{ marginTop: 0 }}>
        Reported in every briefing, counted as <strong>sessions containing the event</strong> rather
        than event count — an event that fires several times per visit would otherwise overstate how
        many people did it.
      </p>

      {error && <Banner tone="critical">{error}</Banner>}
      {notice && <Banner tone="info">{notice}</Banner>}

      <div className="controls" style={{ marginTop: 16 }}>
        <button type="button" className="button" onClick={save} disabled={saving || !dirty}>
          {saving ? 'Saving…' : 'Save events'}
        </button>
        <button
          type="button"
          className="button secondary"
          onClick={() => {
            setEvents([...events, blankEvent()])
            setEditing(events.length)
            setDirty(true)
          }}
        >
          Add event
        </button>
        {dirty && <span className="section__note">Unsaved changes</span>}
      </div>

      {events.length === 0 ? (
        <div className="card">
          <p className="placeholder">No tracked events — the briefing will report metrics only.</p>
        </div>
      ) : (
        <ConfigList>
          {events.map((event, index) => {
            const liveness = pulse[event.event_name]
            const chips: RowChip[] = (coverage[event.event_name] ?? []).map(coverageChip)
            if (liveness?.window_below_normal_gap) {
              chips.unshift({ key: 'drift', text: 'window too tight', tone: 'warning' })
            }
            if (!event.enabled) chips.unshift({ key: 'off', text: 'Off', tone: 'off' })
            return (
              <ConfigRow
                key={index}
                name={event.label || event.event_name || 'New event'}
                code={event.event_name}
                summary={summarise(event, liveness)}
                chips={chips}
                dimmed={!event.enabled}
                active={editing === index}
                onOpen={() => setEditing(index)}
              />
            )
          })}
        </ConfigList>
      )}

      <Drawer
        open={open !== undefined}
        title={open ? open.label || open.event_name || 'New event' : ''}
        subtitle={open?.event_name || 'Not yet named'}
        onClose={() => setEditing(null)}
        footer={
          <>
            <button
              type="button"
              className="button danger"
              onClick={() => {
                setEvents(events.filter((_, i) => i !== editing))
                setEditing(null)
                setDirty(true)
              }}
            >
              Remove
            </button>
            <button type="button" className="button" onClick={save} disabled={saving}>
              {saving ? 'Saving…' : 'Save events'}
            </button>
          </>
        }
      >
        {open !== undefined && editing !== null && (
          <EventEditor
            event={open}
            // Only other events can be a denominator; an event cannot divide by itself.
            candidates={events
              .filter((_, i) => i !== editing)
              .map((item) => item.event_name)
              .filter(Boolean)}
            liveness={pulse[open.event_name]}
            coverage={coverage[open.event_name] ?? []}
            onChange={(patch) => update(editing, patch)}
          />
        )}
      </Drawer>
    </>
  )
}

interface EditorProps {
  event: TrackedEvent
  candidates: string[]
  /** This event's measured rhythm, absent until the pulse has been fetched. */
  liveness?: EventLiveness
  /** Measured coverage for this event's parameters. Counts only, never values. */
  coverage: ParameterCoverage[]
  onChange: (patch: Partial<TrackedEvent>) => void
}

function EventEditor({ event, candidates, liveness, coverage, onChange }: EditorProps) {
  const suggested = liveness?.suggested_silent_after_hours ?? null
  const applied = event.silent_after_hours ?? liveness?.watch_hours ?? null
  const drifted = liveness?.window_below_normal_gap ?? false

  return (
    <>
      <div className="drawer__grid">
        <label className="field">
          <span className="field__label">GA4 event name</span>
          <input
            value={event.event_name}
            placeholder="paywall_view"
            maxLength={100}
            spellCheck={false}
            onChange={(e) => onChange({ event_name: e.target.value })}
          />
        </label>

        <label className="field">
          <span className="field__label">Shown as</span>
          <input
            value={event.label}
            placeholder="Paywall views"
            maxLength={80}
            onChange={(e) => onChange({ label: e.target.value })}
          />
        </label>

        <label className="field">
          <span className="field__label">Funnel rate vs</span>
          <select
            value={event.rate_against ?? ''}
            onChange={(e) => onChange({ rate_against: e.target.value || null })}
          >
            <option value="">No funnel rate</option>
            {candidates.map((name) => (
              <option key={name} value={name}>
                {name}
              </option>
            ))}
          </select>
        </label>

        <label className="field">
          <span className="field__label">Rate shown as</span>
          <input
            value={event.rate_label}
            placeholder="Subscribe click-through"
            maxLength={80}
            disabled={!event.rate_against}
            onChange={(e) => onChange({ rate_label: e.target.value })}
          />
        </label>
      </div>

      {event.rate_against && (
        <p className="breakdown__note" style={{ margin: 0 }}>
          Reported as sessions with <code>{event.event_name || 'this event'}</code> ÷ sessions with{' '}
          <code>{event.rate_against}</code>.
        </p>
      )}

      {/* The tracking pulse's thresholds. Separated from the fields above because they
          govern a different question — not how the event is reported, but when its
          absence is worth raising. Both are optional: blank means the shared default. */}
      <p className="drawer__section">When silence is a fault</p>

      <div className="drawer__grid">
        <label className="field">
          <span className="field__label">Silent after (hours)</span>
          <input
            type="number"
            min={1}
            max={48}
            value={event.silent_after_hours ?? ''}
            placeholder="6 (shared default)"
            onChange={(e) =>
              onChange({ silent_after_hours: e.target.value ? Number(e.target.value) : null })
            }
          />
        </label>

        <label className="field">
          <span className="field__label">Only if last week had</span>
          <input
            type="number"
            min={1}
            value={event.minimum_expected ?? ''}
            placeholder="5 (shared default)"
            onChange={(e) =>
              onChange({ minimum_expected: e.target.value ? Number(e.target.value) : null })
            }
          />
        </label>
      </div>

      <p className="breakdown__note" style={{ margin: 0 }}>
        How long this event may go without firing before the daybook’s pulse calls it broken, and
        how much the same hours a week earlier must have carried for that to mean anything. A break
        is caught roughly one window after GA4 delivers the hours, so tighten this for a
        high-volume event. The pulse reports each event’s longest normal gap — set this above that.
      </p>

      {liveness && (
        <p
          className={drifted ? 'breakdown__note breakdown__note--warning' : 'breakdown__note'}
          style={{ margin: 0 }}
        >
          {drifted ? (
            <>
              <strong>This window is below the event’s own rhythm.</strong> Watched over {applied}h,
              but it goes quiet for up to {liveness.longest_normal_gap}h normally
              {liveness.false_alerts_per_week
                ? `, which would fire about ${liveness.false_alerts_per_week}× a week on nothing`
                : ''}
              .
            </>
          ) : (
            <>
              Measured: quiet for up to {liveness.longest_normal_gap ?? '—'}h normally
              {liveness.false_alerts_per_week
                ? `, ~${liveness.false_alerts_per_week} false alerts/week at ${applied}h`
                : ''}
              .
            </>
          )}{' '}
          {suggested !== null && suggested !== event.silent_after_hours && (
            <button
              type="button"
              className="link-button"
              onClick={() => onChange({ silent_after_hours: suggested })}
            >
              Use suggested {suggested}h
            </button>
          )}
        </p>
      )}

      <p className="drawer__section">Required parameters</p>

      <label className="field">
        <span className="field__label">Event-scoped custom dimensions</span>
        <input
          value={event.parameters.join(', ')}
          placeholder="paywall_id, page_type"
          onChange={(e) =>
            onChange({
              parameters: e.target.value
                .split(',')
                .map((name) => name.trim())
                .filter(Boolean),
            })
          }
        />
        <span className="field__note">
          Alerts when they stop arriving &mdash; volume is unchanged when that happens, so nothing
          else notices. Each must be registered in GA4 Admin &rarr; Custom definitions; values are
          never backfilled. Only whether a value arrived is read, never the value.
        </span>
      </label>

      {coverage.map((report) => (
        <p
          key={report.parameter}
          className={
            report.state === 'not_set_only' ||
            report.state === 'dropped' ||
            report.state === 'barely_arriving'
              ? 'breakdown__note breakdown__note--warning'
              : 'breakdown__note'
          }
          style={{ margin: 0 }}
        >
          <strong>{report.parameter}</strong>{' '}
          {report.coverage_percent === null
            ? report.detail
            : `on ${report.coverage_percent}% of occurrences` +
              (report.previous_coverage_percent !== null
                ? ` (${report.previous_coverage_percent}% a week ago)`
                : '') +
              `, ${report.distinct_values} distinct value${report.distinct_values === 1 ? '' : 's'}.`}
        </p>
      ))}

      <label className="checkbox">
        <input
          type="checkbox"
          checked={event.enabled}
          onChange={(e) => onChange({ enabled: e.target.checked })}
        />
        Enabled
      </label>
    </>
  )
}
